






































































































































































import Vue from 'vue';
import { Grid } from '@progress/kendo-vue-grid';
import http, { handleApiResponse } from '@/services/http';
import ApiResponse from '@/models/ApiResponse';
import eventBus from '@/services/eventBus';
import IconButton from '@/components/IconButton.vue';
import CheckBox from '@/components/CheckBox.vue';
import LookupComboBox from './LookupComboBox.vue';
import basket, { ReconBasketStore } from '../services/ReconBasketStore';
import { t } from "@/plugins/i18n";

/** A tranzakció-böngésző ApiSearchPageForLedger `name` propja — az eventBus csatornák előtagja */
const SEARCH_NAME = 'ledger-transactions';

interface Data {
  open: boolean;
  withAccno: boolean;
  basket: ReconBasketStore;
  years: any[];
  invoiceMirrors: any[];
  startYear: string | null;
  startAccno: string;
  inProgress: any[];
}

interface Methods {
  finalize(): void;
  startMode(): void;
  exitMode(): void;
  onStartAccnoSelected(item: any): void;
  loadInvoiceMirrors(): Promise<void>;
  loadInProgress(): Promise<void>;
  continueRecon(id: number | null): Promise<void>;
  onContinueSelected(item: any): void;
  openMenu(): void;
  formatTotal(value: any): string;
  remarkText(item: any): string;
}

interface Computed {
  canFinalize: boolean;
  columns: any[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  name: 'ReconBasketMenu',

  components: { Grid, IconButton, CheckBox, LookupComboBox },

  data(): Data {
    return {
      open: false,
      withAccno: !!basket.commonAccno,
      basket,
      years: [],
      invoiceMirrors: [],
      startYear: String(new Date().getFullYear()),
      startAccno: '',
      inProgress: [],
    };
  },

  watch: {
    // Ha a kosár tartalma egy fksz-re szűkül/bővül, a mód-ajánlás követi
    'basket.commonAccno'(value: string | null) {
      // Folytatott csoportnál a mód a csoportból jön, a kosár tartalma nem írja át
      if (this.basket.continued) return;
      this.withAccno = !!value;
    },

    // Az indító fksz-választó listája a választott évhez kötött
    startYear() {
      this.startAccno = '';
      this.loadInvoiceMirrors();
      this.loadInProgress();
    },
  },

  async created() {
    // A böngésző piros capsule-ja innen nyitja a kosarat
    eventBus.$on(`${SEARCH_NAME}:recon-basket-open`, this.openMenu);

    this.years = await http
      .get<ApiResponse<any[]>>('general-ledger-recon/years')
      .then(handleApiResponse);
    if (!this.years.some((y: any) => y.year === this.startYear)) {
      this.startYear = this.years.length ? this.years[0].year : null;
    }
    await this.loadInvoiceMirrors();
    await this.loadInProgress();
  },

  beforeDestroy() {
    eventBus.$off(`${SEARCH_NAME}:recon-basket-open`, this.openMenu);
  },

  computed: {
    canFinalize() {
      return this.basket.positiveItems.length > 0 && this.basket.negativeItems.length > 0;
    },

    // A Megjegyzés a szélesség nélküli flex-oszlop: a maradék helyet tölti ki, ellipszissel + tooltippel
    columns: () => [
      // Kuka a sor elején (user-kérés 2026-09-10), a GL böngésző művelet-oszlop mintájára
      { field: '', width: '50px', cell: 'removeCell' },
      { field: 'accDate', title: t("Datum"), width: '100px', className: 'right_text' },
      { field: 'accno', title: t("Fksz"), width: '90px' },
      { field: 'total', title: t("Osszeg"), cell: 'totalCell', width: '110px' },
      { field: 'remark', title: t("Megjegyzes"), cell: 'remarkCell' },
    ],
  },

  methods: {
    // A csoport létrehozása a meglévő szerkesztőben történik; a kosár a fromBasket jelzésre töltődik be
    finalize() {
      this.open = false;
      // Folytatott csoport: a meglévő fej szerkesztője, a tételek a kosárból
      if (this.basket.continued) {
        this.$router.push({
          path: `/ledger-matching/edit/${this.basket.continued.id}`,
          query: { fromBasket: 'true' },
        });
        return;
      }
      this.$router.push({
        path: '/ledger-matching/create',
        query: { fromBasket: 'true', withAccno: String(this.withAccno) },
      });
    },

    // A szűrőket a böngésző (ApiSearchPageForLedger) állítja — ott van a szűrőállapot és a visszaállítás
    startMode() {
      if (!this.startYear) return;
      eventBus.$emit(`${SEARCH_NAME}:recon-mode-start`, { year: Number(this.startYear), accno: this.startAccno });
      this.open = false;
    },

    exitMode() {
      eventBus.$emit(`${SEARCH_NAME}:recon-mode-exit`);
    },

    onStartAccnoSelected(item) {
      this.startAccno = item && item.accno && item.accno !== '-' ? String(item.accno) : '';
    },

    async loadInvoiceMirrors() {
      if (!this.startYear) {
        this.invoiceMirrors = [];
        return;
      }
      try {
        this.invoiceMirrors = await http
          .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/invoice-mirror/${this.startYear}`)
          .then(handleApiResponse);
      } catch {
        this.invoiceMirrors = [];
      }
    },

    async loadInProgress() {
      if (!this.startYear) {
        this.inProgress = [];
        return;
      }
      try {
        const rows = await http
          .get<ApiResponse<any[]>>(`general-ledger-recon/in-progress/${this.startYear}`)
          .then(handleApiResponse);
        // A lookup szöveges mezője: kód, kód nélküli csoportnál a megjegyzés; az egyenleg formázva oszlopba
        this.inProgress = rows.map((r: any) => ({
          ...r,
          label: String(r.code || r.remark || r.id),
          accno: r.accno && String(r.accno).trim() !== '-' ? String(r.accno).trim() : '',
          balanceText: this.formatTotal(r.balance),
        }));
      } catch {
        this.inProgress = [];
      }
    },

    // A lookup a rács-sorra kattintva a teljes sort adja; gépelt, listában nem szereplő szöveg (nincs id) nem választ
    onContinueSelected(item) {
      if (!item || !item.id) return;
      this.continueRecon(Number(item.id));
    },

    // Meglévő, feldolgozás alatti csoport folytatása: tételei a kosárba, majd a mód indul az év + fksz szűrővel
    async continueRecon(id) {
      if (!id) {
        this.basket.clear();
        return;
      }
      const head = this.inProgress.find((r: any) => r.id === id);
      if (!head) return;
      try {
        const items = await http
          .get<ApiResponse<any>>(`general-ledger-recon/${id}/items`)
          .then(handleApiResponse);
        const accno = head.accno ? String(head.accno) : '-';
        this.basket.loadFromRecon(
          { id: head.id, code: head.code, remark: head.remark, accno, year: Number(head.yearName) },
          [...items.positiveItems, ...items.negativeItems],
        );
        this.withAccno = accno !== '-';
        eventBus.$emit(`${SEARCH_NAME}:recon-mode-start`, { year: Number(head.yearName), accno: accno !== '-' ? accno : '' });
        this.open = false;
      } catch (e) {
        eventBus.$emit('error', t("Transactions_AzOsszepontozasTeteleinekBetolteseNemSikerult"));
      }
    },

    openMenu() {
      this.open = true;
    },

    formatTotal(value) {
      return new Intl.NumberFormat('hu-HU').format(Number(value || 0));
    },

    // A tétel megjegyzése; ha üres ('-'), a fej gazdasági eseménye, hogy a sor felismerhető maradjon
    remarkText(item) {
      const remark = String(item.remark || '').trim();
      return remark && remark !== '-' ? remark : String(item.accEvent || '');
    },
  },
});
